// 📄 pages/MiEspacio/sections/ContactosSection.tsx
import { useEffect, useState } from 'react';
import { Plus, Trash2, Pencil, Contact2, Mail, Phone, X } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext.tsx';
import { Contacto, ContactoData } from '../../../types/miEspacio.ts';
import { getContactos, saveContacto, deleteContacto } from '../../../services/miEspacioService.ts';
import ConfirmDialog from '../../../components/ui/ConfirmDialog.tsx';
import { MiEspacioSeccionProps, etiquetaContexto } from '../miEspacioContexto.ts';

const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";
const labelClass = "block text-xs font-medium text-gray-400 mb-1";

const emptyForm: ContactoData = { nombreCompleto: '', empresa: '', rol: '', email: '', telefono: '', notas: '' };

export default function ContactosSection({ contextosConsulta, contextoNuevo, proyectosPorId, mostrarProyecto }: MiEspacioSeccionProps) {
  const { user } = useAuth();
  const [contactos, setContactos] = useState<Contacto[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [form, setForm] = useState<ContactoData>(emptyForm);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aEliminar, setAEliminar] = useState<Contacto | null>(null);

  const cargar = async () => {
    if (!user) return;
    setLoading(true);
    try {
      setContactos(await getContactos(user.uid, contextosConsulta));
    } catch (err) {
      console.error('Error cargando contactos:', err);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, [user?.uid, contextosConsulta.join(',')]);

  const openCreate = () => {
    setEditingId(null);
    setEditingProjectId(contextoNuevo);
    setForm(emptyForm);
    setError(null);
    setFormOpen(true);
  };

  const openEdit = (contacto: Contacto) => {
    setEditingId(contacto.id);
    setEditingProjectId(contacto.projectId);
    setForm({ nombreCompleto: contacto.nombreCompleto, empresa: contacto.empresa, rol: contacto.rol, email: contacto.email, telefono: contacto.telefono, notas: contacto.notas });
    setError(null);
    setFormOpen(true);
  };

  const handleGuardar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setGuardando(true);
    setError(null);
    try {
      await saveContacto(user.uid, editingId, form, editingProjectId);
      setFormOpen(false);
      cargar();
    } catch (err: any) {
      setError(err.message || 'Error al guardar el contacto.');
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async () => {
    if (!user || !aEliminar) return;
    try {
      await deleteContacto(user.uid, aEliminar.id, aEliminar.projectId);
      setAEliminar(null);
      cargar();
    } catch (err) {
      console.error('Error al eliminar el contacto:', err);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        {!formOpen && (
          <button onClick={openCreate} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-all">
            <Plus className="w-4 h-4" /> Nuevo contacto
          </button>
        )}
      </div>

      {formOpen && (
        <form onSubmit={handleGuardar} className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
              <Contact2 className="w-4 h-4 text-blue-400" /> {editingId ? 'Editar contacto' : 'Nuevo contacto'}
            </h2>
            <button type="button" onClick={() => setFormOpen(false)} className="text-gray-500 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>

          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Nombre completo</label>
              <input className={inputClass} value={form.nombreCompleto} onChange={(e) => setForm({ ...form, nombreCompleto: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>Empresa / Cliente</label>
              <input className={inputClass} value={form.empresa} onChange={(e) => setForm({ ...form, empresa: e.target.value })} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Rol</label>
              <input className={inputClass} value={form.rol} onChange={(e) => setForm({ ...form, rol: e.target.value })} placeholder="Basis, Consultor Ariba, PM..." />
            </div>
            <div>
              <label className={labelClass}>Teléfono</label>
              <input className={inputClass} value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
            </div>
          </div>

          <div>
            <label className={labelClass}>Correo electrónico</label>
            <input type="email" className={inputClass} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>

          <div>
            <label className={labelClass}>Notas</label>
            <textarea className={`${inputClass} min-h-[70px]`} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} />
          </div>

          <div className="flex justify-end">
            <button type="submit" disabled={guardando} className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-all">
              {guardando ? 'Guardando...' : 'Guardar contacto'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-sm text-gray-400">Cargando contactos...</p>
      ) : contactos.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <Contact2 className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">Aún no tienes contactos guardados.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {contactos.map((contacto) => (
            <div key={contacto.id} className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-white">{contacto.nombreCompleto}</h3>
                  <p className="text-xs text-gray-500">{[contacto.rol, contacto.empresa].filter(Boolean).join(' · ')}</p>
                  {mostrarProyecto && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 inline-block mt-1">
                      {etiquetaContexto(contacto.projectId, proyectosPorId)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button onClick={() => openEdit(contacto)} className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => setAEliminar(contacto)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {contacto.email && (
                <p className="text-xs text-gray-400 flex items-center gap-1.5"><Mail className="w-3 h-3" /> {contacto.email}</p>
              )}
              {contacto.telefono && (
                <p className="text-xs text-gray-400 flex items-center gap-1.5"><Phone className="w-3 h-3" /> {contacto.telefono}</p>
              )}
              {contacto.notas && <p className="text-xs text-gray-400 pt-1 border-t border-white/10">{contacto.notas}</p>}
            </div>
          ))}
        </div>
      )}

      {aEliminar && (
        <ConfirmDialog
          title="¿Eliminar este contacto?"
          message={`"${aEliminar.nombreCompleto}" se eliminará permanentemente.`}
          confirmLabel="Eliminar"
          danger
          onConfirm={handleEliminar}
          onCancel={() => setAEliminar(null)}
        />
      )}
    </div>
  );
}
